import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Mail, Users, Send, FileText, Zap, BarChart3, Search, Plus, Trash2,
  ChevronLeft, ChevronRight, ChevronUp, Eye, Pencil, X, Download, Upload,
  CheckCircle, XCircle, AlertTriangle, Clock, Play, Pause, ArrowLeft,
  ChevronDown, Copy, TestTube, MessageSquare, Gift, Cog, RefreshCw,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import AdminMenu from "../components/AdminMenu";

const DEFAULT_REWARD_TEMPLATE = "We appreciate your support, and feedback to help us create a unique, and powerful service. To help you get started please claim your bonus coins within {{claim_hrs}} of receiving this email.";

function formatClaimHrs(hrs) {
  const h = Number(hrs);
  if (!Number.isFinite(h) || h <= 0) return `${hrs} hours`;
  if (h < 24) return `${h} hours`;
  if (h < 168) {
    const d = Math.round(h / 24);
    return `${h} hours (${d} day${d === 1 ? "" : "s"})`;
  }
  if (h < 720) {
    const w = Math.round(h / 168);
    return `${h} hours (${w} week${w === 1 ? "" : "s"})`;
  }
  const m = Math.round(h / 720);
  return `${h} hours (${m} month${m === 1 ? "" : "s"})`;
}

function TestSendModal({ onClose, onSend, accentBtn, accentBorder, label }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!email.trim()) { toast.error("Enter an email address"); return; }
    setSending(true);
    try {
      await onSend(email.trim());
      toast.success(`Test email sent to ${email.trim()}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="test-send-modal">
      <div className={`w-full max-w-sm bg-divine-black-light border ${accentBorder} rounded-lg p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-cinzel">{label || "Send Test Email"}</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
        </div>
        <p className="text-white/40 text-xs mb-3">Subject will be prefixed with <code className="text-white/60">[TEST]</code>. All variables (reward tokens, tracking links) are rendered with real values.</p>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="recipient@example.com"
          className="bg-white/5 border-white/10 mb-4"
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          autoFocus
          data-testid="test-send-email-input"
        />
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={onClose} className="border-white/10 text-white/50">Cancel</Button>
          <Button size="sm" className={accentBtn} onClick={handleSend} disabled={sending} data-testid="test-send-submit-btn">
            {sending ? "Sending..." : "Send Test"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function detectEmailTheme(html) {
  const bgMatch = html.match(/background(?:-color)?:\s*([#\w]+)/);
  if (bgMatch) {
    let hex = bgMatch[1].replace("#", "");
    if (hex.length === 3) hex = hex.split("").map(c => c + c).join("");
    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
      const r = parseInt(hex.slice(0, 2), 16), g = parseInt(hex.slice(2, 4), 16), b = parseInt(hex.slice(4, 6), 16);
      if ((0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.4) {
        return { bg: "#1a1a1d", border: "#333", text: "#d4d4d8" };
      }
    }
  }
  return { bg: "#fafafa", border: "#e5e5e5", text: "#333" };
}

function buildRewardBlockPreview(templateText, coinAmount, claimHrs, emailHtml = "") {
  if (!coinAmount || coinAmount <= 0) return "";
  const msg = (templateText || DEFAULT_REWARD_TEMPLATE).replace(/\{\{claim_hrs\}\}/g, formatClaimHrs(claimHrs || 48));
  const t = detectEmailTheme(emailHtml);
  const formatted = Number(coinAmount).toLocaleString("en-US");
  return `<div style="margin:24px 0;padding:20px;border:1px solid ${t.border};border-radius:8px;background:${t.bg};text-align:center;"><p style="margin:0 0 16px;color:${t.text};font-size:15px;line-height:1.5;">${msg}</p><a href="#" style="display:inline-block;padding:12px 28px;background:#d4af37;color:#fff;text-decoration:none;border-radius:6px;font-weight:bold;font-size:15px;">Claim ${formatted} Coins</a></div>`;
}

function buildRewardTextPreview(templateText, coinAmount, claimHrs) {
  if (!coinAmount || coinAmount <= 0) return "";
  const msg = (templateText || DEFAULT_REWARD_TEMPLATE).replace(/\{\{claim_hrs\}\}/g, formatClaimHrs(claimHrs || 48));
  const formatted = Number(coinAmount).toLocaleString("en-US");
  return `\n\n${msg}\n\nClaim ${formatted} Coins: [reward link]\n`;
}

const API_TABS = [
  { id: "subscribers", label: "Subscribers", icon: Users },
  { id: "campaigns", label: "Campaigns", icon: Send },
  { id: "templates", label: "Templates", icon: FileText },
  { id: "sequences", label: "Sequences", icon: Zap },
  { id: "comments", label: "Comments", icon: MessageSquare },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "sys-emails", label: "Sys Emails", icon: Cog },
];

const SUB_STATUS_COLORS = {
  subscribed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  unsubscribed: "bg-red-500/15 text-red-400 border-red-500/30",
  bounced: "bg-orange-500/15 text-orange-400 border-orange-500/30",
};

const CAMPAIGN_STATUS_COLORS = {
  draft: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  sending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  sent: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  failed: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function MailerPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCyber = theme === "agent_eros_1";
  const accent = isCyber ? "text-cyan-400" : "text-divine-gold";
  const accentBg = isCyber ? "bg-cyan-500/10 border-cyan-500/30" : "bg-divine-gold/10 border-divine-gold/30";
  const accentBorder = isCyber ? "border-cyan-500/30" : "border-divine-gold/30";
  const accentBtn = isCyber ? "bg-cyan-600 hover:bg-cyan-700" : "bg-divine-gold hover:bg-divine-gold/90";

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    const validTabs = ["subscribers", "campaigns", "templates", "sequences", "comments", "analytics", "sys-emails"];
    return validTabs.includes(tabParam) ? tabParam : "subscribers";
  });

  if (user?.role !== "admin") {
    return <div className="min-h-screen bg-divine-black flex items-center justify-center text-white/50">Admin access required</div>;
  }

  return (
    <div className="min-h-screen bg-divine-black text-white" data-testid="mailer-page">
      <Toaster position="top-center" richColors />
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Mail className={`w-6 h-6 ${accent}`} />
          <h1 className="text-2xl font-cinzel" data-testid="mailer-title">Mailing List Manager</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/waitlist")} className="border-white/10 text-white/60 hover:text-white gap-1.5" data-testid="goto-waitlist-btn">
              <Users className="w-4 h-4" /> Waitlist
            </Button>
            <AdminMenu />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-1" data-testid="mailer-tabs">
          {API_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? `${accentBg} ${accent}`
                    : "text-white/50 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "subscribers" && <SubscribersTab accent={accent} accentBorder={accentBorder} accentBtn={accentBtn} />}
        {activeTab === "campaigns" && <CampaignsTab accent={accent} accentBorder={accentBorder} accentBtn={accentBtn} />}
        {activeTab === "templates" && <TemplatesTab accent={accent} accentBorder={accentBorder} accentBtn={accentBtn} />}
        {activeTab === "sequences" && <SequencesTab accent={accent} accentBorder={accentBorder} accentBtn={accentBtn} />}
        {activeTab === "comments" && <CommentsTab accent={accent} accentBorder={accentBorder} accentBtn={accentBtn} />}
        {activeTab === "analytics" && <AnalyticsTab accent={accent} accentBorder={accentBorder} />}
        {activeTab === "sys-emails" && <SysEmailsTab accent={accent} accentBorder={accentBorder} accentBtn={accentBtn} />}
      </div>
    </div>
  );
}

/* ═══════════════════════ SUBSCRIBERS TAB ═══════════════════════ */
function SubscribersTab({ accent, accentBorder, accentBtn }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [allTags, setAllTags] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showAdd, setShowAdd] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "25" });
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter) params.set("status", statusFilter);
      if (tagFilter) params.set("tag", tagFilter);
      const res = await authAxios.get(`/mailer/subscribers?${params}`);
      setItems(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setCounts(res.data.counts || {});
      setAllTags(res.data.all_tags || []);
    } catch (err) {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, tagFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [search, statusFilter, tagFilter]);
  useEffect(() => { setSelected(new Set()); }, [page, search, statusFilter, tagFilter]);

  const handleImport = async (type) => {
    setImporting(true);
    try {
      const endpoint = type === "waitlist" ? "/mailer/subscribers/import-waitlist" : "/mailer/subscribers/import-users";
      const tag = type === "waitlist" ? "waitlist" : "registered";
      const res = await authAxios.post(endpoint, { tag });
      toast.success(`Imported ${res.data.imported} subscribers (${res.data.skipped} already existed)`);
      fetchList();
    } catch (err) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this subscriber?")) return;
    try {
      await authAxios.delete(`/mailer/subscribers/${id}`);
      toast.success("Subscriber deleted");
      fetchList();
    } catch { toast.error("Delete failed"); }
  };

  const handleBulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length || !window.confirm(`Delete ${ids.length} subscriber(s)?`)) return;
    try {
      await authAxios.post("/mailer/subscribers/bulk-delete", { ids });
      toast.success(`Deleted ${ids.length} subscribers`);
      setSelected(new Set());
      fetchList();
    } catch { toast.error("Bulk delete failed"); }
  };

  const toggleAll = () => {
    setSelected(selected.size === items.length ? new Set() : new Set(items.map(i => i.id)));
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div data-testid="subscribers-tab">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input placeholder="Search email or name..." className="pl-10 bg-divine-black-light border-white/10" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sub-search" />
        </div>
        <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)} data-testid="sub-status-filter">
          <SelectTrigger className="w-[180px] bg-divine-black-light border-white/10 text-white/70">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-divine-black-light border-white/10">
            <SelectItem value="__all__" className="text-white/70 focus:bg-white/10 focus:text-white">All Status</SelectItem>
            <SelectItem value="subscribed" className="text-white/70 focus:bg-white/10 focus:text-white">Subscribed ({counts.subscribed || 0})</SelectItem>
            <SelectItem value="unsubscribed" className="text-white/70 focus:bg-white/10 focus:text-white">Unsubscribed ({counts.unsubscribed || 0})</SelectItem>
            <SelectItem value="bounced" className="text-white/70 focus:bg-white/10 focus:text-white">Bounced ({counts.bounced || 0})</SelectItem>
          </SelectContent>
        </Select>
        {allTags.length > 0 && (
          <Select value={tagFilter || "__all__"} onValueChange={(v) => setTagFilter(v === "__all__" ? "" : v)} data-testid="sub-tag-filter">
            <SelectTrigger className="w-[160px] bg-divine-black-light border-white/10 text-white/70">
              <SelectValue placeholder="All Tags" />
            </SelectTrigger>
            <SelectContent className="bg-divine-black-light border-white/10">
              <SelectItem value="__all__" className="text-white/70 focus:bg-white/10 focus:text-white">All Tags</SelectItem>
              {allTags.map(t => <SelectItem key={t} value={t} className="text-white/70 focus:bg-white/10 focus:text-white">{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button size="sm" className={accentBtn} onClick={() => setShowAdd(true)} data-testid="add-subscriber-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add
        </Button>
        <Button size="sm" variant="outline" className="border-white/10 text-white/60" onClick={() => handleImport("waitlist")} disabled={importing} data-testid="import-waitlist-btn">
          <Download className="w-4 h-4 mr-1.5" /> Import Waitlist
        </Button>
        <Button size="sm" variant="outline" className="border-white/10 text-white/60" onClick={() => handleImport("users")} disabled={importing} data-testid="import-users-btn">
          <Download className="w-4 h-4 mr-1.5" /> Import Users
        </Button>
        {selected.size > 0 && (
          <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 ml-auto" onClick={handleBulkDelete} data-testid="bulk-delete-subs-btn">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete {selected.size}
          </Button>
        )}
        <span className="text-white/40 text-sm ml-auto">{total} subscriber{total !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <div className={`border ${accentBorder} rounded-lg overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="px-3 py-3 w-10"><input type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} className="rounded border-white/30 bg-transparent cursor-pointer" /></th>
                <th className="text-left px-4 py-3 text-white/50 font-medium">Email</th>
                <th className="text-left px-4 py-3 text-white/50 font-medium hidden sm:table-cell">Name</th>
                <th className="text-left px-4 py-3 text-white/50 font-medium hidden md:table-cell">Tags</th>
                <th className="text-left px-4 py-3 text-white/50 font-medium">Status</th>
                <th className="text-left px-4 py-3 text-white/50 font-medium hidden lg:table-cell">Source</th>
                <th className="text-left px-4 py-3 text-white/50 font-medium hidden lg:table-cell">Date</th>
                <th className="text-right px-4 py-3 text-white/50 font-medium w-16"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-white/30">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-white/30">No subscribers found</td></tr>
              ) : items.map((item) => (
                <tr key={item.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`sub-row-${item.id}`}>
                  <td className="px-3 py-3"><input type="checkbox" checked={selected.has(item.id)} onChange={() => setSelected(prev => { const n = new Set(prev); n.has(item.id) ? n.delete(item.id) : n.add(item.id); return n; })} className="rounded border-white/30 bg-transparent cursor-pointer" /></td>
                  <td className="px-4 py-3 text-white/90">
                    <span className="inline-flex items-center gap-1.5">
                      {item.email}
                      {item.email_verified && <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white/60 hidden sm:table-cell">{item.name || "-"}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">{(item.tags || []).map(t => <span key={t} className="px-2 py-0.5 text-xs bg-white/5 text-white/50 rounded-full border border-white/10">{t}</span>)}</div>
                  </td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs border ${SUB_STATUS_COLORS[item.status] || SUB_STATUS_COLORS.subscribed}`}>{item.status}</span></td>
                  <td className="px-4 py-3 text-white/40 text-xs hidden lg:table-cell">{item.source || "-"}</td>
                  <td className="px-4 py-3 text-white/40 text-xs hidden lg:table-cell">{formatDate(item.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button variant="ghost" size="sm" onClick={() => setEditSub(item)} className="h-8 w-8 p-0 text-white/40 hover:text-white" data-testid={`edit-sub-${item.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="h-8 w-8 p-0 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-white/40 text-sm">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-white/10"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border-white/10"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAdd && <AddSubscriberModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); fetchList(); }} accentBtn={accentBtn} accentBorder={accentBorder} />}

      {/* Edit Modal */}
      {editSub && <EditSubscriberModal subscriber={editSub} onClose={() => setEditSub(null)} onSaved={() => { setEditSub(null); fetchList(); }} accentBtn={accentBtn} accentBorder={accentBorder} />}
    </div>
  );
}

function AddSubscriberModal({ onClose, onAdded, accentBtn, accentBorder }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      await authAxios.post("/mailer/subscribers", {
        email: email.trim(),
        name: name.trim(),
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        source: "manual",
      });
      toast.success("Subscriber added");
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`w-full max-w-md bg-divine-black-light border ${accentBorder} rounded-lg p-6`} data-testid="add-subscriber-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-cinzel">Add Subscriber</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0"><X className="w-5 h-5" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Email *</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/5 border-white/10" data-testid="add-sub-email" />
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="add-sub-name" />
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Tags (comma-separated)</label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. beta, vip" className="bg-white/5 border-white/10" data-testid="add-sub-tags" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" size="sm" className={accentBtn} disabled={saving} data-testid="submit-subscriber-btn">{saving ? "Adding..." : "Add Subscriber"}</Button>
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="border-white/10 text-white/50">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditSubscriberModal({ subscriber, onClose, onSaved, accentBtn, accentBorder }) {
  const [name, setName] = useState(subscriber.name || "");
  const [tags, setTags] = useState((subscriber.tags || []).join(", "));
  const [status, setStatus] = useState(subscriber.status || "subscribed");
  const [emailVerified, setEmailVerified] = useState(!!subscriber.email_verified);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.patch(`/mailer/subscribers/${subscriber.id}`, {
        name: name.trim(),
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        status,
        email_verified: emailVerified,
      });
      toast.success("Subscriber updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`w-full max-w-md bg-divine-black-light border ${accentBorder} rounded-lg p-6`} data-testid="edit-subscriber-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-cinzel">Edit Subscriber</h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0"><X className="w-5 h-5" /></Button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Email</label>
            <Input value={subscriber.email} disabled className="bg-white/5 border-white/10 opacity-50 cursor-not-allowed" data-testid="edit-sub-email" />
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="edit-sub-name" />
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Tags (comma-separated)</label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. beta, vip" className="bg-white/5 border-white/10" data-testid="edit-sub-tags" />
            {tags && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.split(",").map(t => t.trim()).filter(Boolean).map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-white/5 text-white/60 rounded-full border border-white/10">
                    {t}
                    <button
                      onClick={() => setTags(tags.split(",").map(x => x.trim()).filter(x => x && x !== t).join(", "))}
                      className="text-white/30 hover:text-red-400 ml-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Status</label>
            <Select value={status} onValueChange={setStatus} data-testid="edit-sub-status">
              <SelectTrigger className="w-full bg-white/5 border-white/10 text-white/70">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-divine-black-light border-white/10">
                <SelectItem value="subscribed" className="text-white/70 focus:bg-white/10 focus:text-white">Subscribed</SelectItem>
                <SelectItem value="unsubscribed" className="text-white/70 focus:bg-white/10 focus:text-white">Unsubscribed</SelectItem>
                <SelectItem value="bounced" className="text-white/70 focus:bg-white/10 focus:text-white">Bounced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-3 cursor-pointer py-1" data-testid="edit-sub-verified">
            <input
              type="checkbox"
              checked={emailVerified}
              onChange={(e) => setEmailVerified(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-white/5 accent-emerald-500"
            />
            <span className="text-white/60 text-sm">Email Verified</span>
            {emailVerified && <CheckCircle className="w-4 h-4 text-emerald-400" />}
          </label>
          <div className="text-white/30 text-xs pt-1">
            <span>Source: {subscriber.source || "-"}</span>
            <span className="mx-2">|</span>
            <span>Added: {subscriber.created_at ? new Date(subscriber.created_at).toLocaleDateString() : "-"}</span>
          </div>
          <div className="flex gap-3 pt-2">
            <Button size="sm" className={accentBtn} onClick={handleSave} disabled={saving} data-testid="save-subscriber-btn">{saving ? "Saving..." : "Save Changes"}</Button>
            <Button variant="outline" size="sm" onClick={onClose} className="border-white/10 text-white/50">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════ CAMPAIGNS TAB ═══════════════════════ */
function CampaignsTab({ accent, accentBorder, accentBtn }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const [viewCampaign, setViewCampaign] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/mailer/campaigns?limit=50");
      setCampaigns(res.data.items || []);
    } catch { toast.error("Failed to load campaigns"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await authAxios.delete(`/mailer/campaigns/${id}`);
      toast.success("Campaign deleted");
      fetchCampaigns();
    } catch { toast.error("Delete failed"); }
  };

  const handleCopy = async (id) => {
    try {
      const res = await authAxios.post(`/mailer/campaigns/${id}/copy`);
      toast.success("Campaign duplicated as new draft");
      fetchCampaigns();
      // Open the copy in the editor immediately
      if (res.data?.id) setEditCampaign(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Copy failed"); }
  };

  const handleSend = async (id) => {
    if (!window.confirm("Send this campaign to all matching subscribers?")) return;
    try {
      await authAxios.post(`/mailer/campaigns/${id}/send`);
      toast.success("Campaign queued for sending");
      fetchCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
  };

  const handleTest = async (id) => {
    const email = prompt("Send test email to:");
    if (!email) return;
    try {
      await authAxios.post(`/mailer/campaigns/${id}/test`, { email });
      toast.success(`Test email sent to ${email}`);
    } catch { toast.error("Test send failed"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (showComposer || editCampaign || viewCampaign) {
    return <CampaignComposer
      campaign={editCampaign || viewCampaign}
      readOnly={!!viewCampaign}
      onClose={() => { setShowComposer(false); setEditCampaign(null); setViewCampaign(null); }}
      onSaved={() => { setShowComposer(false); setEditCampaign(null); setViewCampaign(null); fetchCampaigns(); }}
      accent={accent} accentBorder={accentBorder} accentBtn={accentBtn}
    />;
  }

  return (
    <div data-testid="campaigns-tab">
      <div className="flex items-center gap-3 mb-4">
        <Button size="sm" className={accentBtn} onClick={() => setShowComposer(true)} data-testid="create-campaign-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Campaign
        </Button>
        <span className="text-white/40 text-sm ml-auto">{campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}</span>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="text-center text-white/30 py-12">Loading...</div>
        ) : campaigns.length === 0 ? (
          <div className="text-center text-white/30 py-12">No campaigns yet</div>
        ) : campaigns.map((c) => (
          <div key={c.id} className={`border ${accentBorder} rounded-lg p-4 hover:bg-white/[0.02] transition-colors`} data-testid={`campaign-card-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white/90 font-medium truncate">{c.name}</h3>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${CAMPAIGN_STATUS_COLORS[c.status] || CAMPAIGN_STATUS_COLORS.draft}`}>{c.status}</span>
                </div>
                <p className="text-white/50 text-sm truncate mb-2">Subject: {c.subject}</p>
                {c.last_error && c.status === "sent" && (
                  <div className="mb-2 px-2 py-1.5 rounded border border-red-500/30 bg-red-500/10 text-red-300 text-xs" data-testid={`campaign-last-error-${c.id}`}>
                    <span className="font-medium">Send error:</span> {c.last_error}
                  </div>
                )}
                <div className="flex flex-wrap gap-4 text-xs text-white/40">
                  <span>Sent: {c.stats?.sent || 0}</span>
                  <span>Opened: {c.stats?.opened || 0}</span>
                  <span>Clicked: {c.stats?.clicked || 0}</span>
                  <span>{formatDate(c.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {c.status === "draft" && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => handleTest(c.id)} className="h-8 px-2 text-white/50" title="Send test" data-testid={`test-campaign-${c.id}`}><TestTube className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditCampaign(c)} className="h-8 px-2 text-white/50" title="Edit" data-testid={`edit-campaign-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleCopy(c.id)} className="h-8 px-2 text-white/50" title="Duplicate" data-testid={`copy-campaign-${c.id}`}><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleSend(c.id)} className="h-8 px-2 text-emerald-400" title="Send" data-testid={`send-campaign-${c.id}`}><Play className="w-4 h-4" /></Button>
                  </>
                )}
                {c.status === "sent" && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => setViewCampaign(c)} className="h-8 px-2 text-white/50" title="View" data-testid={`view-campaign-${c.id}`}><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleCopy(c.id)} className="h-8 px-2 text-white/50" title="Duplicate" data-testid={`copy-campaign-${c.id}`}><Copy className="w-4 h-4" /></Button>
                  </>
                )}
                <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="h-8 px-2 text-red-400" title="Delete" data-testid={`delete-campaign-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CampaignComposer({ campaign, onClose, onSaved, accent, accentBorder, accentBtn, readOnly = false }) {
  const [name, setName] = useState(campaign?.name || "");
  const [subject, setSubject] = useState(campaign?.subject || "");
  const [htmlContent, setHtmlContent] = useState(campaign?.html_content || "");
  const [fromName, setFromName] = useState(campaign?.from_name || "Agent Eros");
  const [fromEmail, setFromEmail] = useState(campaign?.from_email || "noreply@agenteros.ai");
  const [filterTags, setFilterTags] = useState(campaign?.recipient_filter?.tags?.join(", ") || "");
  const [coinReward, setCoinReward] = useState(campaign?.coin_reward || 0);
  const [rewardExpiryHours, setRewardExpiryHours] = useState(campaign?.reward_expiry_hours || 48);
  const [webUrl, setWebUrl] = useState(campaign?.web_url || "");
  const [rewardTemplate, setRewardTemplate] = useState(campaign?.reward_template_text || DEFAULT_REWARD_TEMPLATE);
  const [isPublic, setIsPublic] = useState(campaign?.public !== undefined ? !!campaign.public : true);
  const [abEnabled, setAbEnabled] = useState(!!campaign?.ab_test);
  const [abSubject, setAbSubject] = useState(campaign?.ab_test?.variant_b_subject || "");
  const [abSplit, setAbSplit] = useState(campaign?.ab_test?.split_pct || 50);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showTestSend, setShowTestSend] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !htmlContent.trim()) {
      toast.error("Name, subject, and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        subject,
        html_content: htmlContent,
        from_name: fromName || undefined,
        from_email: fromEmail || undefined,
        recipient_filter: filterTags.trim() ? { tags: filterTags.split(",").map(t => t.trim()).filter(Boolean) } : {},
        ab_test: abEnabled ? { variant_b_subject: abSubject, split_pct: abSplit } : null,
        coin_reward: coinReward,
        reward_expiry_hours: rewardExpiryHours,
        web_url: webUrl.trim() || undefined,
        reward_template_text: rewardTemplate,
        public: isPublic,
      };
      if (campaign?.id) {
        await authAxios.put(`/mailer/campaigns/${campaign.id}`, payload);
      } else {
        await authAxios.post("/mailer/campaigns", payload);
      }
      toast.success(campaign ? "Campaign updated" : "Campaign created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="campaign-composer">
      <button onClick={onClose} className="flex items-center gap-2 text-white/50 hover:text-white mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to campaigns
      </button>
      <h2 className="text-lg font-cinzel mb-4">{readOnly ? "View Sent Campaign" : campaign ? "Edit Campaign" : "New Campaign"}</h2>

      <fieldset disabled={readOnly} className={readOnly ? "opacity-90 pointer-events-none" : ""}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Form */}
        <div className="space-y-4">
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Campaign Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="campaign-name" />
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Subject Line *</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-white/5 border-white/10" data-testid="campaign-subject" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">From Name</label>
              <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Agent Eros" className="bg-white/5 border-white/10" data-testid="campaign-from-name" />
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">From Email</label>
              <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="noreply@agenteros.ai" className="bg-white/5 border-white/10" data-testid="campaign-from-email" />
            </div>
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Filter by Tags (comma-separated, leave blank for all)</label>
            <Input value={filterTags} onChange={(e) => setFilterTags(e.target.value)} placeholder="e.g. beta, vip" className="bg-white/5 border-white/10" data-testid="campaign-filter-tags" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Read Coin Reward</label>
              <Input type="number" min={0} value={coinReward} onChange={(e) => setCoinReward(Number(e.target.value))} placeholder="0" className="bg-white/5 border-white/10" data-testid="campaign-coin-reward" />
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Claim Expiry (hrs)</label>
              <Input type="number" min={1} value={rewardExpiryHours} onChange={(e) => setRewardExpiryHours(Number(e.target.value))} placeholder="48" className="bg-white/5 border-white/10" data-testid="campaign-reward-expiry" />
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Web URL</label>
              <Input value={webUrl} onChange={(e) => setWebUrl(e.target.value)} placeholder="Auto-generated from subject" className="bg-white/5 border-white/10" data-testid="campaign-web-url" />
            </div>
          </div>
          <p className="text-white/30 text-xs -mt-2">Use <code className="text-white/50">{`{{name}}`}</code> for full name, <code className="text-white/50">{`{{first_name}}`}</code> for first name only, <code className="text-white/50">{`{{coin_reward}}`}</code> for the reward block, <code className="text-white/50">{`{{unsubscribe}}`}</code> / <code className="text-white/50">{`{{unsubscribe_url}}`}</code> for unsubscribe link, <code className="text-white/50">{`{{terms_link}}`}</code> for T&C, <code className="text-white/50">{`{{reg_link}}`}</code> for registration link. <code className="text-white/50">{`{{name}}`}</code> and <code className="text-white/50">{`{{first_name}}`}</code> also work in the subject line.</p>
          {coinReward > 0 && (
            <div>
              <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Reward Message</label>
              <textarea
                value={rewardTemplate}
                onChange={(e) => setRewardTemplate(e.target.value)}
                className="w-full h-20 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/70 resize-vertical"
                data-testid="campaign-reward-template"
              />
              <p className="text-white/30 text-xs mt-1">Use <code className="text-white/50">{`{{claim_hrs}}`}</code> for expiry hours</p>
            </div>
          )}

          {/* Public / Subscribers-only visibility */}
          <div className={`border ${accentBorder} rounded-lg p-4`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="mt-1 rounded border-white/30 bg-transparent"
                data-testid="campaign-public-toggle"
              />
              <div>
                <div className="text-sm text-white/80">Public blog page</div>
                <p className="text-white/40 text-xs mt-0.5">
                  {isPublic
                    ? "Anyone can view the /blog page for this campaign — Google-indexable, shareable."
                    : "Subscribers only — /blog page requires a reward token from the email link (admins bypass)."}
                </p>
              </div>
            </label>
          </div>

          {/* A/B Test */}
          <div className={`border ${accentBorder} rounded-lg p-4`}>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={abEnabled} onChange={(e) => setAbEnabled(e.target.checked)} className="rounded border-white/30 bg-transparent" />
              <span className="text-sm text-white/70">Enable A/B Test</span>
            </label>
            {abEnabled && (
              <div className="space-y-3">
                <div>
                  <label className="text-white/40 text-xs uppercase block mb-1">Variant B Subject</label>
                  <Input value={abSubject} onChange={(e) => setAbSubject(e.target.value)} className="bg-white/5 border-white/10" data-testid="ab-subject" />
                </div>
                <div>
                  <label className="text-white/40 text-xs uppercase block mb-1">Split % (variant B receives)</label>
                  <Input type="number" min={10} max={90} value={abSplit} onChange={(e) => setAbSplit(Number(e.target.value))} className="bg-white/5 border-white/10 w-24" data-testid="ab-split" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: HTML Editor + Preview */}
        <div className="space-y-4">
          <div>
            <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">HTML Content *</label>
            <textarea
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              className="w-full h-64 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 font-mono resize-vertical"
              data-testid="campaign-html-content"
            />
          </div>
          {htmlContent && (
            <div>
              <label className="text-white/40 text-xs uppercase tracking-wider block mb-1">Preview</label>
              <div className="border border-white/10 rounded-lg overflow-hidden bg-white">
                <iframe
                  srcDoc={htmlContent
                    .replace(/\{\{name\}\}/g, "John Doe")
                    .replace(/\{\{unsubscribe_url\}\}/g, "#")
                    .replace(/\{\{unsubscribe\}\}/g, "#")
                    .replace(/\{\{terms_link\}\}/g, "/terms")
                    .replace(/\{\{reg_link\}\}/g, "/auth?tab=register")
                    .replace(/\{\{coin_reward\}\}/g, coinReward > 0 ? buildRewardBlockPreview(rewardTemplate, coinReward, rewardExpiryHours, htmlContent) : "")
                  }
                  title="Preview"
                  className="w-full h-64"
                  sandbox="allow-same-origin"
                  data-testid="campaign-preview"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      </fieldset>

      <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
        {!readOnly && (
          <Button size="sm" className={accentBtn} onClick={handleSave} disabled={saving} data-testid="save-campaign-btn">{saving ? "Saving..." : "Save Campaign"}</Button>
        )}
        {campaign?.id && !readOnly && (
          <Button size="sm" variant="outline" onClick={() => setShowTestSend(true)} className="border-white/10 text-white/60 hover:text-white" data-testid="campaign-test-send-btn">
            <TestTube className="w-3.5 h-3.5 mr-1.5" /> Send Test
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onClose} className="border-white/10 text-white/50">{readOnly ? "Close" : "Cancel"}</Button>
      </div>

      {showTestSend && campaign?.id && (
        <TestSendModal
          label="Test Campaign Email"
          accentBtn={accentBtn}
          accentBorder={accentBorder}
          onClose={() => setShowTestSend(false)}
          onSend={async (email) => { await authAxios.post(`/mailer/campaigns/${campaign.id}/test-send`, { email }); }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════ TEMPLATES TAB ═══════════════════════ */
function TemplatesTab({ accent, accentBorder, accentBtn }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/mailer/templates");
      setTemplates(res.data.templates || []);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await authAxios.delete(`/mailer/templates/${id}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch { toast.error("Delete failed"); }
  };

  if (showEditor || editTemplate) {
    return <TemplateEditor
      template={editTemplate}
      onClose={() => { setShowEditor(false); setEditTemplate(null); }}
      onSaved={() => { setShowEditor(false); setEditTemplate(null); fetchTemplates(); }}
      accent={accent} accentBorder={accentBorder} accentBtn={accentBtn}
    />;
  }

  return (
    <div data-testid="templates-tab">
      <div className="flex items-center gap-3 mb-4">
        <Button size="sm" className={accentBtn} onClick={() => setShowEditor(true)} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Template
        </Button>
        <span className="text-white/40 text-sm ml-auto">{templates.length} template{templates.length !== 1 ? "s" : ""}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center text-white/30 py-12">Loading...</div>
        ) : templates.length === 0 ? (
          <div className="col-span-full text-center text-white/30 py-12">No templates yet</div>
        ) : templates.map((t) => (
          <div key={t.id} className={`border ${accentBorder} rounded-lg p-4 hover:bg-white/[0.02] transition-colors`} data-testid={`template-card-${t.id}`}>
            <h3 className="text-white/90 font-medium mb-1 truncate">{t.name}</h3>
            <p className="text-white/40 text-xs mb-2">{t.category || "general"}</p>
            {t.variables?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {t.variables.map(v => <span key={v} className="px-1.5 py-0.5 text-xs bg-white/5 text-white/40 rounded">{`{{${v}}}`}</span>)}
              </div>
            )}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setEditTemplate(t)} className="h-8 px-2 text-white/50"><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="h-8 px-2 text-red-400"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateEditor({ template, onClose, onSaved, accent, accentBorder, accentBtn }) {
  const [name, setName] = useState(template?.name || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [htmlBody, setHtmlBody] = useState(template?.html_body || DEFAULT_NEWSLETTER_HTML);
  const [variables, setVariables] = useState(template?.variables?.join(", ") || "name, email, unsubscribe_url");
  const [category, setCategory] = useState(template?.category || "general");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !htmlBody.trim()) {
      toast.error("Name and HTML body are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        subject,
        html_body: htmlBody,
        variables: variables.split(",").map(v => v.trim()).filter(Boolean),
        category,
      };
      if (template?.id) {
        await authAxios.put(`/mailer/templates/${template.id}`, payload);
      } else {
        await authAxios.post("/mailer/templates", payload);
      }
      toast.success(template ? "Template updated" : "Template created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="template-editor">
      <button onClick={onClose} className="flex items-center gap-2 text-white/50 hover:text-white mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to templates
      </button>
      <h2 className="text-lg font-cinzel mb-4">{template ? "Edit Template" : "New Template"}</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-white/40 text-xs uppercase block mb-1">Template Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="template-name" />
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase block mb-1">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-white/5 border-white/10" data-testid="template-subject" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/40 text-xs uppercase block mb-1">Category</label>
              <Select value={category} onValueChange={setCategory} data-testid="template-category">
                <SelectTrigger className="w-full bg-white/5 border-white/10 text-white/70">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-divine-black-light border-white/10">
                  <SelectItem value="general" className="text-white/70 focus:bg-white/10 focus:text-white">General</SelectItem>
                  <SelectItem value="newsletter" className="text-white/70 focus:bg-white/10 focus:text-white">Newsletter</SelectItem>
                  <SelectItem value="promotional" className="text-white/70 focus:bg-white/10 focus:text-white">Promotional</SelectItem>
                  <SelectItem value="transactional" className="text-white/70 focus:bg-white/10 focus:text-white">Transactional</SelectItem>
                  <SelectItem value="welcome" className="text-white/70 focus:bg-white/10 focus:text-white">Welcome</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/40 text-xs uppercase block mb-1">Variables</label>
              <Input value={variables} onChange={(e) => setVariables(e.target.value)} placeholder="name, email" className="bg-white/5 border-white/10" data-testid="template-variables" />
            </div>
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase block mb-1">HTML Body *</label>
            <textarea
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              className="w-full h-72 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 font-mono resize-vertical"
              data-testid="template-html-body"
            />
          </div>
        </div>

        <div>
          <label className="text-white/40 text-xs uppercase block mb-1">Live Preview</label>
          <div className="border border-white/10 rounded-lg overflow-hidden bg-white sticky top-4">
            <iframe srcDoc={htmlBody} title="Template Preview" className="w-full h-[500px]" sandbox="allow-same-origin" data-testid="template-preview" />
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
        <Button size="sm" className={accentBtn} onClick={handleSave} disabled={saving} data-testid="save-template-btn">{saving ? "Saving..." : "Save Template"}</Button>
        <Button size="sm" variant="outline" onClick={onClose} className="border-white/10 text-white/50">Cancel</Button>
      </div>
    </div>
  );
}

const DEFAULT_NEWSLETTER_HTML = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"></head>
<body style="margin:0;padding:0;background:#0a0a0a;font-family:'Segoe UI',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#0a0a0a;padding:40px 0;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0" style="background:#161616;border:1px solid #2a2a2a;border-radius:12px;overflow:hidden;">
        <tr><td style="background:linear-gradient(135deg,#7c3aed,#a855f7);padding:32px 40px;text-align:center;">
          <h1 style="margin:0;color:#fff;font-size:24px;letter-spacing:1px;">Agent Eros</h1>
          <p style="margin:8px 0 0;color:rgba(255,255,255,0.8);font-size:14px;">Newsletter</p>
        </td></tr>
        <tr><td style="padding:40px;">
          <p style="color:#e0e0e0;font-size:16px;line-height:1.6;">Hello <strong style="color:#a855f7;">{{name}}</strong>,</p>
          <p style="color:#b0b0b0;font-size:15px;line-height:1.6;">Your content goes here.</p>
        </td></tr>
        <tr><td style="background:#0f0f0f;padding:24px 40px;border-top:1px solid #2a2a2a;">
          <p style="color:#666;font-size:12px;margin:0 0 8px;text-align:center;">Agent Eros</p>
          <p style="color:#555;font-size:11px;margin:0;text-align:center;"><a href="{{unsubscribe_url}}" style="color:#555;text-decoration:underline;">Unsubscribe</a></p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

/* ═══════════════════════ SEQUENCES TAB ═══════════════════════ */
function SequencesTab({ accent, accentBorder, accentBtn }) {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editSequence, setEditSequence] = useState(null);
  const [running, setRunning] = useState(false);

  const fetchSequences = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/mailer/sequences");
      setSequences(res.data.sequences || []);
    } catch { toast.error("Failed to load sequences"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSequences(); }, [fetchSequences]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sequence?")) return;
    try {
      await authAxios.delete(`/mailer/sequences/${id}`);
      toast.success("Sequence deleted");
      fetchSequences();
    } catch { toast.error("Delete failed"); }
  };

  const handleToggle = async (seq) => {
    try {
      await authAxios.put(`/mailer/sequences/${seq.id}`, { active: !seq.active });
      toast.success(seq.active ? "Sequence paused" : "Sequence activated");
      fetchSequences();
    } catch { toast.error("Toggle failed"); }
  };

  const handleRunBatch = async () => {
    const activeCount = sequences.filter(s => s.active).length;
    const pausedCount = sequences.filter(s => !s.active).length;
    const msg = `Running ${activeCount} sequence${activeCount !== 1 ? "s" : ""}${pausedCount ? ` (${pausedCount} paused, will be skipped)` : ""}. This will enroll new subscribers and send pending emails.`;
    if (!window.confirm(msg)) return;
    setRunning(true);
    try {
      const res = await authAxios.post("/mailer/sequences/run-batch");
      const d = res.data;
      const parts = [`${d.sequences_run} sequence${d.sequences_run !== 1 ? "s" : ""} run`];
      if (d.sequences_paused) parts.push(`${d.sequences_paused} paused`);
      if (d.enrolled) parts.push(`${d.enrolled} enrolled`);
      if (d.removed) parts.push(`${d.removed} removed`);
      parts.push(`${d.emails_sent} emails sent`);
      if (d.errors) parts.push(`${d.errors} errors`);
      toast.success(parts.join(", "));
      fetchSequences();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  if (showBuilder || editSequence) {
    return <SequenceBuilder
      sequence={editSequence}
      onClose={() => { setShowBuilder(false); setEditSequence(null); }}
      onSaved={() => { setShowBuilder(false); setEditSequence(null); fetchSequences(); }}
      accent={accent} accentBorder={accentBorder} accentBtn={accentBtn}
    />;
  }

  const calcRate = (num, denom) => denom > 0 ? `${Math.round(num / denom * 100)}%` : "–";

  return (
    <div data-testid="sequences-tab">
      <div className="flex items-center gap-3 mb-4">
        <Button size="sm" className={accentBtn} onClick={() => setShowBuilder(true)} data-testid="create-sequence-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Sequence
        </Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleRunBatch} disabled={running} data-testid="run-batch-btn">
          <Play className="w-4 h-4 mr-1.5" /> {running ? "Running..." : "Run Now"}
        </Button>
        <span className="text-white/40 text-sm ml-auto">{sequences.length} sequence{sequences.length !== 1 ? "s" : ""}</span>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="text-center text-white/30 py-12">Loading...</div>
        ) : sequences.length === 0 ? (
          <div className="text-center text-white/30 py-12">No sequences yet</div>
        ) : sequences.map((seq) => {
          const s = seq.stats || {};
          return (
            <div key={seq.id} className={`border ${accentBorder} rounded-lg p-4`} data-testid={`sequence-card-${seq.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white/90 font-medium">{seq.name}</h3>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${seq.active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>
                      {seq.active ? "Active" : "Paused"}
                    </span>
                  </div>
                  <p className="text-white/40 text-sm mb-2">Tags: {seq.trigger_value || "none"} | {seq.steps?.length || 0} steps | {s.enrolled || 0} enrolled</p>
                  <div className="flex flex-wrap gap-4 text-xs text-white/40">
                    <span>Sent: <span className="text-white/70">{s.total_sent || 0}</span></span>
                    <span>Delivered: <span className="text-white/70">{s.delivered || 0}</span></span>
                    <span>Opened: <span className="text-white/70">{s.opened || 0}</span></span>
                    <span>Clicked: <span className="text-white/70">{s.clicked || 0}</span></span>
                    <span>Bounced: <span className="text-white/70">{s.bounced || 0}</span></span>
                    <span>Open Rate: <span className={accent}>{calcRate(s.opened || 0, s.delivered || 0)}</span></span>
                    <span>Click Rate: <span className={accent}>{calcRate(s.clicked || 0, s.delivered || 0)}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => handleToggle(seq)} className={`h-8 px-2 gap-1 text-xs ${seq.active ? "text-amber-400 hover:text-amber-300" : "text-emerald-400 hover:text-emerald-300"}`} data-testid={`toggle-seq-${seq.id}`}>
                    {seq.active ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Resume</>}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditSequence(seq)} className="h-8 px-2 text-white/50"><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(seq.id)} className="h-8 px-2 text-red-400"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SequenceBuilder({ sequence, onClose, onSaved, accent, accentBorder, accentBtn }) {
  const [name, setName] = useState(sequence?.name || "");
  const [triggerValue, setTriggerValue] = useState(sequence?.trigger_value || "");
  const [fromName, setFromName] = useState(sequence?.from_name || "Agent Eros");
  const [fromEmail, setFromEmail] = useState(sequence?.from_email || "noreply@agenteros.ai");
  const [defaultCoinReward, setDefaultCoinReward] = useState(sequence?.default_coin_reward || 0);
  const [rewardExpiryHours, setRewardExpiryHours] = useState(sequence?.reward_expiry_hours || 48);
  const [rewardTemplate, setRewardTemplate] = useState(sequence?.reward_template_text || DEFAULT_REWARD_TEMPLATE);
  const [publicCta, setPublicCta] = useState(sequence?.public_cta || "");
  const [steps, setSteps] = useState(sequence?.steps?.length ? sequence.steps : [{ delay_hours: 0, subject: "", html_content: "" }]);
  const [saving, setSaving] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);
  const [previewMode, setPreviewMode] = useState("html");
  const [testSendStepIdx, setTestSendStepIdx] = useState(null);

  const addStep = () => setSteps([...steps, { delay_hours: 24, subject: "", html_content: "" }]);
  const removeStep = (idx) => setSteps(steps.filter((_, i) => i !== idx));
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState("append");
  const [importFile, setImportFile] = useState(null);
  const [importError, setImportError] = useState("");

  const exportSteps = () => {
    const exportData = steps.map((s, i) => ({
      email_no: i + 1,
      delay_hours: s.delay_hours || 0,
      subject: s.subject || "",
      html_content: s.html_content || "",
      text_content: s.text_content || "",
      web_url: s.web_url || "",
      public: !!s.public,
      seo_meta: s.seo_meta || "",
    }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name || "sequence").replace(/\s+/g, "-").toLowerCase()}-steps.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${exportData.length} steps`);
  };

  const handleImport = () => {
    if (!importFile) { setImportError("Please select a JSON file"); return; }
    setImportError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const raw = JSON.parse(e.target.result);
        const arr = Array.isArray(raw) ? raw : raw.steps ? raw.steps : null;
        if (!arr || !Array.isArray(arr) || arr.length === 0) {
          setImportError("JSON must be an array of step objects"); return;
        }
        // Detect format and normalize
        const normalized = arr.map((item, i) => {
          // Simple format: { email_no, subject, html, plain_txt, delay }
          if ("html" in item || "plain_txt" in item || "delay" in item) {
            return {
              delay_hours: item.delay ?? item.delay_hours ?? 0,
              subject: item.subject || "",
              html_content: item.html || item.html_content || "",
              text_content: item.plain_txt || item.text_content || "",
              web_url: item.web_url || "",
              public: !!item.public,
              seo_meta: item.seo_meta || "",
            };
          }
          // Export format: { delay_hours, subject, html_content, text_content, web_url, public, seo_meta }
          return {
            delay_hours: item.delay_hours ?? 0,
            subject: item.subject || "",
            html_content: item.html_content || "",
            text_content: item.text_content || "",
            web_url: item.web_url || "",
            public: !!item.public,
            seo_meta: item.seo_meta || "",
          };
        });
        // Sort by email_no if present
        if (arr[0]?.email_no !== undefined) {
          const sorted = [...arr].sort((a, b) => (a.email_no || 0) - (b.email_no || 0));
          normalized.length = 0;
          sorted.forEach(item => {
            normalized.push({
              delay_hours: item.delay ?? item.delay_hours ?? 0,
              subject: item.subject || "",
              html_content: item.html || item.html_content || "",
              text_content: item.plain_txt || item.text_content || "",
              web_url: item.web_url || "",
              public: !!item.public,
              seo_meta: item.seo_meta || "",
            });
          });
        }
        if (importMode === "overwrite") {
          setSteps(normalized);
        } else {
          setSteps(prev => [...prev, ...normalized]);
        }
        toast.success(`${importMode === "overwrite" ? "Replaced with" : "Appended"} ${normalized.length} steps`);
        setShowImportModal(false);
        setImportFile(null);
      } catch (err) {
        setImportError("Invalid JSON file: " + err.message);
      }
    };
    reader.readAsText(importFile);
  };
  const moveStep = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= steps.length) return;
    const updated = [...steps];
    [updated[idx], updated[next]] = [updated[next], updated[idx]];
    setSteps(updated);
  };
  const updateStep = (idx, field, value) => {
    const updated = [...steps];
    updated[idx] = { ...updated[idx], [field]: value };
    setSteps(updated);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), trigger: "tag_added", trigger_value: triggerValue, from_name: fromName, from_email: fromEmail, steps, default_coin_reward: defaultCoinReward, reward_expiry_hours: rewardExpiryHours, reward_template_text: rewardTemplate, public_cta: publicCta };
      if (sequence?.id) {
        await authAxios.put(`/mailer/sequences/${sequence.id}`, payload);
      } else {
        await authAxios.post("/mailer/sequences", payload);
      }
      toast.success(sequence ? "Sequence updated" : "Sequence created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="sequence-builder">
      <button onClick={onClose} className="flex items-center gap-2 text-white/50 hover:text-white mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to sequences
      </button>
      <h2 className="text-lg font-cinzel mb-4">{sequence ? "Edit Sequence" : "New Sequence"}</h2>

      <div className="space-y-4 max-w-2xl">
        <div>
          <label className="text-white/40 text-xs uppercase block mb-1">Sequence Name *</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-white/5 border-white/10" data-testid="sequence-name" />
        </div>
        <div>
          <label className="text-white/40 text-xs uppercase block mb-1">Tag Names (comma-separated)</label>
          <Input value={triggerValue} onChange={(e) => setTriggerValue(e.target.value)} placeholder="e.g. waitlist, romantic, beta" className="bg-white/5 border-white/10" data-testid="sequence-tags" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-white/40 text-xs uppercase block mb-1">From Name</label>
            <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Agent Eros" className="bg-white/5 border-white/10" data-testid="sequence-from-name" />
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase block mb-1">From Email</label>
            <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="noreply@agenteros.ai" className="bg-white/5 border-white/10" data-testid="sequence-from-email" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-white/40 text-xs uppercase block mb-1">Default Coin Reward</label>
            <Input type="number" min={0} value={defaultCoinReward} onChange={(e) => setDefaultCoinReward(Number(e.target.value))} placeholder="0" className="bg-white/5 border-white/10" data-testid="sequence-default-coin-reward" />
          </div>
          <div>
            <label className="text-white/40 text-xs uppercase block mb-1">Reward Expiry (hrs)</label>
            <Input type="number" min={1} value={rewardExpiryHours} onChange={(e) => setRewardExpiryHours(Number(e.target.value))} placeholder="48" className="bg-white/5 border-white/10" data-testid="sequence-reward-expiry" />
          </div>
        </div>
        <p className="text-white/30 text-xs -mt-2">Use <code className="text-white/50">{`{{name}}`}</code> for full name, <code className="text-white/50">{`{{first_name}}`}</code> for first name only, <code className="text-white/50">{`{{coin_reward}}`}</code> for reward block, <code className="text-white/50">{`{{unsubscribe}}`}</code> / <code className="text-white/50">{`{{unsubscribe_url}}`}</code> for unsubscribe link, <code className="text-white/50">{`{{terms_link}}`}</code> for T&C, <code className="text-white/50">{`{{reg_link}}`}</code> for registration link. Name vars work in subject too. Per-step reward overrides the default.</p>
        {defaultCoinReward > 0 && (
          <div>
            <label className="text-white/40 text-xs uppercase block mb-1">Reward Message</label>
            <textarea
              value={rewardTemplate}
              onChange={(e) => setRewardTemplate(e.target.value)}
              className="w-full h-20 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/70 resize-vertical"
              data-testid="sequence-reward-template"
            />
            <p className="text-white/30 text-xs mt-1">Use <code className="text-white/50">{`{{claim_hrs}}`}</code> for expiry hours</p>
          </div>
        )}

        {/* CTA for Public Pages */}
        <div>
          <label className="text-white/40 text-xs uppercase block mb-1">CTA for Public Pages</label>
          <textarea
            value={publicCta}
            onChange={(e) => setPublicCta(e.target.value)}
            placeholder='<a href="/#signup" style="...">Join the Open Beta</a>'
            className="w-full h-20 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/70 font-mono resize-vertical"
            data-testid="sequence-public-cta"
          />
          <p className="text-white/30 text-xs mt-1">This CTA html will be displayed at the bottom of the public /blog page before the comments</p>
        </div>

        {/* Steps */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-white/40 text-xs uppercase">Email Steps</label>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={exportSteps} className="border-white/10 text-white/50 h-7" data-testid="export-steps-btn" title="Export steps as JSON">
                <Download className="w-3.5 h-3.5 mr-1" /> Export
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowImportModal(true); setImportError(""); setImportFile(null); }} className="border-white/10 text-white/50 h-7" data-testid="import-steps-btn" title="Import steps from JSON">
                <Upload className="w-3.5 h-3.5 mr-1" /> Import
              </Button>
              <Button size="sm" variant="outline" onClick={addStep} className="border-white/10 text-white/50 h-7" data-testid="add-step-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Step
              </Button>
            </div>
          </div>
          <div className="space-y-4">
            {steps.map((step, idx) => (
              <div key={idx} className={`border ${accentBorder} rounded-lg p-4`} data-testid={`step-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-sm font-medium ${accent}`}>Step {idx + 1}</span>
                  <div className="flex items-center gap-1">
                    {idx > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => moveStep(idx, -1)} className="h-7 px-2 text-white/40 hover:text-white" title="Move up" data-testid={`step-${idx}-move-up`}><ChevronUp className="w-3.5 h-3.5" /></Button>
                    )}
                    {idx < steps.length - 1 && (
                      <Button variant="ghost" size="sm" onClick={() => moveStep(idx, 1)} className="h-7 px-2 text-white/40 hover:text-white" title="Move down" data-testid={`step-${idx}-move-down`}><ChevronDown className="w-3.5 h-3.5" /></Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => setPreviewIdx(idx)} className="h-7 px-2 text-white/40 hover:text-white" title="Preview"><Eye className="w-3.5 h-3.5" /></Button>
                    {sequence?.id && (
                      <Button variant="ghost" size="sm" onClick={() => setTestSendStepIdx(idx)} className="h-7 px-2 text-white/40 hover:text-white" title="Send Test" data-testid={`step-${idx}-test-send-btn`}><TestTube className="w-3.5 h-3.5" /></Button>
                    )}
                    {steps.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeStep(idx)} className="h-7 px-2 text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="text-white/40 text-xs block mb-1">Delay (hours)</label>
                    <Input type="number" min={0} value={step.delay_hours} onChange={(e) => updateStep(idx, "delay_hours", Number(e.target.value))} className="bg-white/5 border-white/10" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-white/40 text-xs block mb-1">Subject</label>
                    <Input value={step.subject} onChange={(e) => updateStep(idx, "subject", e.target.value)} className="bg-white/5 border-white/10" />
                  </div>
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">HTML Content</label>
                  <textarea
                    value={step.html_content}
                    onChange={(e) => updateStep(idx, "html_content", e.target.value)}
                    className="w-full h-24 bg-white/5 border border-white/10 rounded px-3 py-2 text-xs text-white/80 font-mono resize-vertical"
                  />
                </div>
                <div className="mt-3">
                  <label className="text-white/40 text-xs block mb-1">Plain Text Content</label>
                  <textarea
                    value={step.text_content || ""}
                    onChange={(e) => updateStep(idx, "text_content", e.target.value)}
                    placeholder="Plain text version for email clients that don't support HTML..."
                    className="w-full h-20 bg-white/5 border border-white/10 rounded px-3 py-2 text-xs text-white/70 font-mono resize-vertical"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-white/40 text-xs block mb-1">Read Coin Reward</label>
                    <Input
                      type="number"
                      min={0}
                      value={step.coin_reward ?? ""}
                      onChange={(e) => updateStep(idx, "coin_reward", e.target.value === "" ? null : Number(e.target.value))}
                      placeholder="Default"
                      className="bg-white/5 border-white/10"
                      data-testid={`step-${idx}-coin-reward`}
                    />
                    <p className="text-white/25 text-xs mt-0.5">Empty = default, 0 = no reward</p>
                  </div>
                  <div>
                    <label className="text-white/40 text-xs block mb-1">Web URL</label>
                    <Input value={step.web_url || ""} onChange={(e) => updateStep(idx, "web_url", e.target.value)} placeholder="Auto-generated from subject" className="bg-white/5 border-white/10" data-testid={`step-${idx}-web-url`} />
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid={`step-${idx}-public-toggle`}>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!step.public}
                      onClick={() => updateStep(idx, "public", !step.public)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${step.public ? "bg-emerald-500" : "bg-white/10"}`}
                      data-testid={`step-${idx}-public-switch`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${step.public ? "translate-x-4" : ""}`} />
                    </button>
                    <span className="text-white/50 text-xs">Public</span>
                  </label>
                  {step.public && <span className="text-emerald-400/60 text-xs">Accessible at /blog/{step.web_url || "..."} without token</span>}
                </div>
                {step.public && (
                  <div className="mt-3">
                    <label className="text-white/40 text-xs block mb-1">SEO Meta</label>
                    <textarea
                      value={step.seo_meta || ""}
                      onChange={(e) => updateStep(idx, "seo_meta", e.target.value)}
                      placeholder={'<meta name="description" content="..." />\n<meta property="og:title" content="..." />'}
                      className="w-full h-20 bg-white/5 border border-white/10 rounded px-3 py-2 text-xs text-white/70 font-mono resize-vertical"
                      data-testid={`step-${idx}-seo-meta`}
                    />
                    <p className="text-white/25 text-xs mt-0.5">HTML meta tags injected into &lt;head&gt; on the public /blog page</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/30 text-xs mt-2">Tip: Use <code className="text-white/50">{"{{name}}"}</code> in subject and body to personalise emails.</p>
      </div>

      <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
        <Button size="sm" className={accentBtn} onClick={handleSave} disabled={saving} data-testid="save-sequence-btn">{saving ? "Saving..." : "Save Sequence"}</Button>
        <Button size="sm" variant="outline" onClick={onClose} className="border-white/10 text-white/50">Cancel</Button>
      </div>

      {/* Step Preview Modal */}
      {previewIdx !== null && steps[previewIdx] && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setPreviewIdx(null)}>
          <div className={`w-full max-w-2xl bg-divine-black-light border ${accentBorder} rounded-lg p-6`} data-testid="step-preview-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-cinzel">Preview — Step {previewIdx + 1}</h3>
              <Button variant="ghost" size="sm" onClick={() => setPreviewIdx(null)} className="h-8 w-8 p-0"><X className="w-5 h-5" /></Button>
            </div>
            <div className="mb-3">
              <label className="text-white/40 text-xs uppercase block mb-1">Subject</label>
              <p className="text-white/80 text-sm bg-white/5 border border-white/10 rounded px-3 py-2">
                {(steps[previewIdx].subject || "(no subject)").replace(/\{\{name\}\}/g, "[name]")}
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-white/40 text-xs uppercase">Email Body</label>
                <div className="flex bg-white/5 rounded-lg p-0.5 border border-white/10">
                  <button onClick={() => setPreviewMode("html")} className={`px-3 py-1 text-xs rounded-md transition-colors ${previewMode === "html" ? `${accentBtn} text-white` : "text-white/50 hover:text-white"}`} data-testid="preview-html-toggle">HTML</button>
                  <button onClick={() => setPreviewMode("text")} className={`px-3 py-1 text-xs rounded-md transition-colors ${previewMode === "text" ? `${accentBtn} text-white` : "text-white/50 hover:text-white"}`} data-testid="preview-text-toggle">Plain Text</button>
                </div>
              </div>
              {steps[previewIdx].html_content ? (
                previewMode === "html" ? (
                  <div className="border border-white/10 rounded-lg overflow-hidden bg-white">
                    <iframe
                      srcDoc={(() => {
                        const stepReward = steps[previewIdx].coin_reward;
                        const resolvedReward = (stepReward === null || stepReward === undefined || stepReward === "") ? defaultCoinReward : Number(stepReward);
                        return steps[previewIdx].html_content
                          .replace(/\{\{name\}\}/g, "John Doe")
                          .replace(/\{\{unsubscribe_url\}\}/g, "#")
                          .replace(/\{\{unsubscribe\}\}/g, "#")
                          .replace(/\{\{terms_link\}\}/g, "/terms")
                          .replace(/\{\{reg_link\}\}/g, "/auth?tab=register")
                          .replace(/\{\{coin_reward\}\}/g, resolvedReward > 0 ? buildRewardBlockPreview(rewardTemplate, resolvedReward, rewardExpiryHours, steps[previewIdx].html_content) : "");
                      })()}
                      title="Step Preview"
                      className="w-full h-[400px]"
                      sandbox="allow-same-origin"
                    />
                  </div>
                ) : (
                  <pre className="w-full h-[400px] overflow-auto bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white/70 whitespace-pre-wrap font-mono">
                    {(() => {
                      const stepReward = steps[previewIdx].coin_reward;
                      const resolvedReward = (stepReward === null || stepReward === undefined || stepReward === "") ? defaultCoinReward : Number(stepReward);
                      const rewardText = resolvedReward > 0 ? buildRewardTextPreview(rewardTemplate, resolvedReward, rewardExpiryHours) : "";
                      const rawText = (steps[previewIdx].text_content
                        ? steps[previewIdx].text_content
                        : steps[previewIdx].html_content
                        .replace(/<style[\s\S]*?<\/style>/gi, "")
                        .replace(/<br\s*\/?>/gi, "\n")
                        .replace(/<\/p>/gi, "\n\n")
                        .replace(/<\/div>/gi, "\n")
                        .replace(/<\/tr>/gi, "\n")
                        .replace(/<\/h[1-6]>/gi, "\n\n")
                        .replace(/<li>/gi, "• ")
                        .replace(/<[^>]+>/g, "")
                        .replace(/&nbsp;/g, " ")
                        .replace(/&amp;/g, "&")
                        .replace(/&lt;/g, "<")
                        .replace(/&gt;/g, ">")
                        .replace(/&rsquo;/g, "'")
                        .replace(/&lsquo;/g, "'")
                        .replace(/&rdquo;/g, '"')
                        .replace(/&ldquo;/g, '"')
                        .replace(/&mdash;/g, "—")
                        .replace(/&ndash;/g, "–")
                        .replace(/\n{3,}/g, "\n\n")
                    ).replace(/\{\{name\}\}/g, "John Doe").replace(/\{\{unsubscribe_url\}\}/g, "#").replace(/\{\{coin_reward\}\}/g, rewardText).trim();
                      return rawText;
                    })()}
                  </pre>
                )
              ) : (
                <p className="text-white/30 text-sm italic py-8 text-center">(no content)</p>
              )}
            </div>
          </div>
        </div>
      )}

      {testSendStepIdx !== null && sequence?.id && (
        <TestSendModal
          label={`Test Step ${testSendStepIdx + 1} Email`}
          accentBtn={accentBtn}
          accentBorder={accentBorder}
          onClose={() => setTestSendStepIdx(null)}
          onSend={async (email) => { await authAxios.post(`/mailer/sequences/${sequence.id}/test-send/${testSendStepIdx}`, { email }); }}
        />
      )}

      {/* Import Steps Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowImportModal(false)} data-testid="import-steps-modal-overlay">
          <div className={`w-full max-w-md bg-divine-black-light border ${accentBorder} rounded-lg p-6`} data-testid="import-steps-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-cinzel">Import Steps</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowImportModal(false)} className="h-8 w-8 p-0" data-testid="import-modal-close-btn"><X className="w-5 h-5" /></Button>
            </div>

            <div className="mb-4">
              <label className="text-white/40 text-xs uppercase block mb-2">Import Mode</label>
              <div className="flex gap-2">
                <button onClick={() => setImportMode("append")} className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${importMode === "append" ? `${accentBtn} border-transparent text-white` : "border-white/10 text-white/50 hover:text-white bg-white/5"}`} data-testid="import-mode-append">
                  Append to existing
                </button>
                <button onClick={() => setImportMode("overwrite")} className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${importMode === "overwrite" ? "bg-red-500/80 border-transparent text-white" : "border-white/10 text-white/50 hover:text-white bg-white/5"}`} data-testid="import-mode-overwrite">
                  Overwrite all
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-white/40 text-xs uppercase block mb-2">JSON File</label>
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportError(""); }}
                className="w-full text-sm text-white/60 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-white/10 file:text-white/70 hover:file:bg-white/20 file:cursor-pointer cursor-pointer"
                data-testid="import-file-input"
              />
              <p className="text-white/30 text-xs mt-1.5">Accepts exported JSON or custom format with <code className="text-white/50">email_no, subject, html, plain_txt, delay</code></p>
            </div>

            {importError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-start gap-2" data-testid="import-error-msg">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{importError}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <Button size="sm" variant="outline" onClick={() => setShowImportModal(false)} className="border-white/10 text-white/50" data-testid="import-cancel-btn">Cancel</Button>
              <Button size="sm" className={accentBtn} onClick={handleImport} disabled={!importFile} data-testid="import-confirm-btn">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Import {importFile ? `(${importFile.name})` : ""}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════ COMMENTS TAB ═══════════════════════ */
function CommentsTab({ accent, accentBorder, accentBtn }) {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editComment, setEditComment] = useState(null);

  const fetchComments = useCallback(async (p = 1, status = "") => {
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (status) params.set("status", status);
      const res = await authAxios.get(`/mailer/comments?${params}`);
      setComments(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
      setPages(res.data.pages || 1);
    } catch { toast.error("Failed to load comments"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchComments(1, ""); }, [fetchComments]);

  const handleStatusChange = async (commentId, newStatus) => {
    try {
      await authAxios.patch(`/mailer/comments/${commentId}`, { status: newStatus });
      toast.success(`Comment ${newStatus}`);
      fetchComments(page, statusFilter);
    } catch { toast.error("Failed to update comment"); }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await authAxios.delete(`/mailer/comments/${commentId}`);
      toast.success("Comment deleted");
      fetchComments(page, statusFilter);
    } catch { toast.error("Failed to delete comment"); }
  };

  const handleEditSave = async () => {
    if (!editComment) return;
    try {
      await authAxios.patch(`/mailer/comments/${editComment.comment_id}`, { content: editComment.content, author_name: editComment.author_name });
      toast.success("Comment updated");
      setEditComment(null);
      fetchComments(page, statusFilter);
    } catch { toast.error("Failed to update comment"); }
  };

  const STATUS_COLORS = {
    pending: "bg-yellow-500/15 text-yellow-400",
    approved: "bg-emerald-500/15 text-emerald-400",
    rejected: "bg-red-500/15 text-red-400",
  };

  if (loading) return <div className="text-center text-white/30 py-12">Loading comments...</div>;

  return (
    <div data-testid="comments-tab">
      {/* Filters */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {["", "pending", "approved", "rejected"].map(s => (
            <button
              key={s || "all"}
              onClick={() => { setStatusFilter(s); fetchComments(1, s); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === s ? `${accent} bg-white/10` : "text-white/40 hover:text-white/60"
              }`}
              data-testid={`comment-filter-${s || "all"}`}
            >
              {s || "All"} {s === "pending" && total > 0 ? `(${comments.filter(c => c.status === "pending").length})` : ""}
            </button>
          ))}
        </div>
        <span className="text-white/30 text-xs">{total} total</span>
      </div>

      {comments.length === 0 ? (
        <div className="text-center text-white/30 py-12">No comments found.</div>
      ) : (
        <div className="space-y-3">
          {comments.map(c => (
            <div key={c.comment_id} className={`border ${accentBorder} rounded-lg p-4`} data-testid={`comment-item-${c.comment_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-white/80 text-sm font-medium">{c.author_name}</span>
                    <span className="text-white/30 text-xs">{c.author_email}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[c.status] || ""}`}>{c.status}</span>
                    {c.parent_id && <span className="text-white/20 text-xs">reply</span>}
                  </div>
                  <p className="text-white/60 text-sm mb-1.5">{c.content}</p>
                  <div className="flex items-center gap-2 text-white/30 text-xs">
                    <a href={`/blog/${c.slug}`} target="_blank" rel="noopener noreferrer" className={`${accent} hover:underline`} data-testid={`comment-read-link-${c.comment_id}`}>
                      /blog/{c.slug}
                    </a>
                    <span>{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {c.status !== "approved" && (
                    <Button size="sm" variant="ghost" onClick={() => handleStatusChange(c.comment_id, "approved")} className="h-7 px-2 text-emerald-400 hover:text-emerald-300" title="Approve" data-testid={`approve-comment-${c.comment_id}`}>
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  )}
                  {c.status !== "rejected" && (
                    <Button size="sm" variant="ghost" onClick={() => handleStatusChange(c.comment_id, "rejected")} className="h-7 px-2 text-red-400 hover:text-red-300" title="Reject" data-testid={`reject-comment-${c.comment_id}`}>
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setEditComment({ ...c })} className="h-7 px-2 text-white/40 hover:text-white" title="Edit" data-testid={`edit-comment-${c.comment_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(c.comment_id)} className="h-7 px-2 text-red-400/60 hover:text-red-400" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
          <span className="text-white/30 text-xs">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-white/10 text-white/50 h-7" disabled={page <= 1} onClick={() => { setPage(page - 1); fetchComments(page - 1, statusFilter); }}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" className="border-white/10 text-white/50 h-7" disabled={page >= pages} onClick={() => { setPage(page + 1); fetchComments(page + 1, statusFilter); }}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Edit Comment Modal */}
      {editComment && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setEditComment(null)}>
          <div className={`w-full max-w-md bg-divine-black-light border ${accentBorder} rounded-lg p-6`} data-testid="edit-comment-modal">
            <h3 className="text-lg font-cinzel mb-4">Edit Comment</h3>
            <div className="mb-3">
              <label className="text-white/40 text-xs uppercase block mb-1">Author Name</label>
              <Input
                value={editComment.author_name || ""}
                onChange={(e) => setEditComment({ ...editComment, author_name: e.target.value })}
                className="bg-white/5 border-white/10"
                data-testid="edit-comment-name"
              />
            </div>
            <p className="text-white/40 text-xs mb-2">{editComment.author_email}</p>
            <label className="text-white/40 text-xs uppercase block mb-1">Content</label>
            <textarea
              value={editComment.content}
              onChange={(e) => setEditComment({ ...editComment, content: e.target.value })}
              className="w-full h-32 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80 resize-vertical"
              data-testid="edit-comment-content"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => setEditComment(null)} className="border-white/10 text-white/50">Cancel</Button>
              <Button size="sm" className={accentBtn} onClick={handleEditSave} data-testid="save-comment-btn">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════ ANALYTICS TAB ═══════════════════════ */
function AnalyticsTab({ accent, accentBorder }) {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPages, setEventPages] = useState(1);
  const [eventFilter, setEventFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await authAxios.get("/mailer/stats/overview");
      setStats(res.data);
    } catch { toast.error("Failed to load stats"); }
  }, []);

  const fetchEvents = useCallback(async (page = 1, filter = "") => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter) params.set("event", filter);
      const res = await authAxios.get(`/mailer/stats/events?${params}`);
      setEvents(res.data.items || []);
      setEventsTotal(res.data.total || 0);
      setEventsPage(res.data.page || 1);
      setEventPages(res.data.pages || 1);
    } catch { toast.error("Failed to load events"); }
  }, []);

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchStats(), fetchEvents(1, "")]);
      setLoading(false);
    };
    init();
  }, [fetchStats, fetchEvents]);

  const handleFilterClick = (filter) => {
    const next = eventFilter === filter ? "" : filter;
    setEventFilter(next);
    setEventsPage(1);
    fetchEvents(1, next);
  };

  const handlePageChange = (newPage) => {
    setEventsPage(newPage);
    fetchEvents(newPage, eventFilter);
  };

  if (loading) return <div className="text-center text-white/30 py-12">Loading analytics...</div>;

  const s = stats?.subscribers || {};
  const c = stats?.campaigns || {};
  const e = stats?.emails || {};

  const perfStats = [
    { key: "delivered", label: "Delivered", value: e.delivered || 0 },
    { key: "opened", label: "Opened", value: e.opened || 0 },
    { key: "clicked", label: "Clicked", value: e.clicked || 0 },
    { key: "bounced", label: "Bounced", value: e.bounced || 0 },
    { key: "unsubscribed", label: "Unsubscribed", value: e.unsubscribed || 0 },
    { key: "", label: "Open Rate", value: `${e.open_rate || 0}%` },
    { key: "", label: "Click Rate", value: `${e.click_rate || 0}%` },
  ];

  return (
    <div data-testid="analytics-tab">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Active Subscribers" value={s.active || 0} accent={accent} accentBorder={accentBorder} testId="stat-active-subs" />
        <StatCard label="Total Subscribers" value={s.total || 0} accent={accent} accentBorder={accentBorder} testId="stat-total-subs" />
        <StatCard label="Campaigns Sent" value={c.sent || 0} accent={accent} accentBorder={accentBorder} testId="stat-campaigns-sent" />
        <StatCard label="Total Emails Sent" value={e.sent || 0} accent={accent} accentBorder={accentBorder} testId="stat-emails-sent" />
      </div>

      {/* Email Performance — clickable filters */}
      <div className={`border ${accentBorder} rounded-lg p-6 mb-8`}>
        <h3 className="text-white/80 font-medium mb-4">Email Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {perfStats.map((ps) => (
            <button
              key={ps.label}
              onClick={() => ps.key && handleFilterClick(ps.key)}
              className={`text-left transition-all rounded-lg p-2 -m-2 ${
                ps.key ? "cursor-pointer hover:bg-white/5" : "cursor-default"
              } ${eventFilter === ps.key && ps.key ? "bg-white/10 ring-1 ring-white/20" : ""}`}
              data-testid={`perf-filter-${ps.key || ps.label.toLowerCase().replace(" ", "-")}`}
            >
              <p className="text-white/40 text-xs mb-0.5">{ps.label}</p>
              <p className="text-white/90 text-lg font-medium">{typeof ps.value === "number" ? ps.value.toLocaleString() : ps.value}</p>
            </button>
          ))}
        </div>
        {eventFilter && (
          <p className="text-white/30 text-xs mt-3">
            Filtering events by: <span className="text-white/60">{eventFilter}</span>
            <button onClick={() => handleFilterClick("")} className="ml-2 text-white/50 hover:text-white underline">clear</button>
          </p>
        )}
      </div>

      {/* Recent Events */}
      <div className={`border ${accentBorder} rounded-lg p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white/80 font-medium">Recent Webhook Events</h3>
          <span className="text-white/30 text-xs">{eventsTotal} total</span>
        </div>
        {events.length === 0 ? (
          <p className="text-white/30 text-sm">No events recorded yet.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="webhook-events-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-3 py-2 text-white/50 font-medium">Event</th>
                    <th className="text-left px-3 py-2 text-white/50 font-medium">Recipient</th>
                    <th className="text-left px-3 py-2 text-white/50 font-medium hidden md:table-cell">Email Sent</th>
                    <th className="text-left px-3 py-2 text-white/50 font-medium hidden sm:table-cell">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-b border-white/5">
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs ${
                          ev.event === "delivered" ? "bg-emerald-500/15 text-emerald-400" :
                          ev.event === "opened" ? "bg-blue-500/15 text-blue-400" :
                          ev.event === "clicked" ? "bg-purple-500/15 text-purple-400" :
                          ev.event === "bounced" || ev.event === "failed" ? "bg-red-500/15 text-red-400" :
                          "bg-zinc-500/15 text-zinc-400"
                        }`}>{ev.event}</span>
                      </td>
                      <td className="px-3 py-2">
                        {ev.subscriber_name && <span className="text-white/80 mr-1">{ev.subscriber_name}</span>}
                        <span className="text-white/40">{ev.recipient}</span>
                      </td>
                      <td className="px-3 py-2 text-white/50 hidden md:table-cell max-w-[280px] truncate" title={ev.email_sent || "-"}>
                        {ev.email_sent || "-"}
                      </td>
                      <td className="px-3 py-2 text-white/40 text-xs hidden sm:table-cell whitespace-nowrap">{ev.created_at ? new Date(ev.created_at).toLocaleString() : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {eventsPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                <span className="text-white/30 text-xs">Page {eventsPage} of {eventsPages}</span>
                <div className="flex gap-2">
                  <Button
                    size="sm" variant="outline"
                    className="border-white/10 text-white/50 h-7"
                    disabled={eventsPage <= 1}
                    onClick={() => handlePageChange(eventsPage - 1)}
                    data-testid="events-prev-page"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    className="border-white/10 text-white/50 h-7"
                    disabled={eventsPage >= eventsPages}
                    onClick={() => handlePageChange(eventsPage + 1)}
                    data-testid="events-next-page"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, accentBorder, testId }) {
  return (
    <div className={`border ${accentBorder} rounded-lg p-4`} data-testid={testId}>
      <p className="text-white/40 text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${accent}`}>{typeof value === "number" ? value.toLocaleString() : value}</p>
    </div>
  );
}


/* ═══════════════════════ SYS EMAILS TAB ═══════════════════════ */
function SysEmailsTab({ accent, accentBorder, accentBtn }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [runUserOpen, setRunUserOpen] = useState(false);
  const [runUserSlug, setRunUserSlug] = useState("");
  const [runUserEmail, setRunUserEmail] = useState("");
  const [busySlug, setBusySlug] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/admin/mailer/sys-emails");
      setRows(r.data.rows || []);
    } catch (err) {
      toast.error("Failed to load sys emails");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const preview = async (slug) => {
    setBusySlug(slug);
    try {
      const r = await authAxios.post(`/admin/mailer/sys-emails/${slug}/preview`);
      setPreviewSubject(r.data.subject);
      setPreviewHtml(r.data.html);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setBusySlug("");
    }
  };

  const sendTest = async (slug) => {
    setBusySlug(slug);
    try {
      const r = await authAxios.post(`/admin/mailer/sys-emails/${slug}/test`);
      toast.success(`Test sent to ${r.data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally {
      setBusySlug("");
    }
  };

  const runForUser = async () => {
    if (!runUserEmail) { toast.error("Enter an email"); return; }
    setBusySlug(runUserSlug);
    try {
      const r = await authAxios.post(`/admin/mailer/sys-emails/${runUserSlug}/run-user`, { email: runUserEmail });
      toast.success(`Digest dispatched to ${r.data.target}`);
      setRunUserOpen(false);
      setRunUserEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusySlug("");
      load();
    }
  };

  const runBatch = async (slug) => {
    if (!window.confirm("Run the full weekly batch now? This will send digests to every eligible affiliate who has opted-in.")) return;
    setBusySlug(slug);
    try {
      const r = await authAxios.post(`/admin/mailer/sys-emails/${slug}/run-batch`, { respect_opt_outs: true });
      toast.success(`Batch: ${r.data.sent} sent, ${r.data.skipped_optout} opt-out, ${r.data.skipped_dormant} dormant`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Batch failed");
    } finally {
      setBusySlug("");
      load();
    }
  };

  return (
    <div data-testid="sys-emails-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-white/60">
          Scheduled + trigger-driven emails. Templates live in <b>Templates</b> tab; edit copy there.
        </p>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className={`border-white/10 text-white/60 hover:text-white gap-1.5`} data-testid="sys-emails-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.slug} className="border border-white/10 rounded-lg p-4 bg-white/[0.02]" data-testid={`sys-email-${r.slug}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-white/90 font-medium">{r.name}</div>
                <div className="text-xs text-white/40 font-mono mt-0.5">{r.slug}</div>
                <div className="text-xs text-white/60 mt-2">{r.trigger_label}</div>
                {r.last_run && (
                  <div className="text-xs text-white/40 mt-1">
                    Last run: <span className="text-white/70">{r.last_run.iso_week}</span> — sent {r.last_run.sent} / eligible {r.last_run.eligible}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] uppercase tracking-wide">
                <StatCell label="Sent (30d)" value={r.stats_30d.sent} />
                <StatCell label="Open %" value={`${r.stats_30d.open_rate}%`} />
                <StatCell label="Click %" value={`${r.stats_30d.click_rate}%`} />
                <StatCell label="Bounced" value={r.stats_30d.delivered ? (r.stats_30d.sent - r.stats_30d.delivered) : 0} />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => preview(r.slug)} disabled={busySlug === r.slug} className="border-white/10 text-white/70 gap-1.5" data-testid={`preview-${r.slug}`}>
                <Eye className="w-3.5 h-3.5" /> Preview
              </Button>
              <Button size="sm" variant="outline" onClick={() => sendTest(r.slug)} disabled={busySlug === r.slug} className="border-white/10 text-white/70 gap-1.5" data-testid={`test-${r.slug}`}>
                <TestTube className="w-3.5 h-3.5" /> Send Test
              </Button>
              {r.trigger_type === "scheduled" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => { setRunUserSlug(r.slug); setRunUserOpen(true); }} disabled={busySlug === r.slug} className="border-white/10 text-white/70 gap-1.5" data-testid={`run-user-${r.slug}`}>
                    <Send className="w-3.5 h-3.5" /> Run For User
                  </Button>
                  <Button size="sm" onClick={() => runBatch(r.slug)} disabled={busySlug === r.slug} className={`${accentBtn} gap-1.5`} data-testid={`run-batch-${r.slug}`}>
                    <Play className="w-3.5 h-3.5" /> Run Full Batch Now
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && !loading && (
          <div className="text-center text-white/30 py-12">No system emails registered</div>
        )}
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)} data-testid="sys-email-preview-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wide">Subject</div>
                <div className="text-white/90 text-sm mt-1">{previewSubject}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(false)}><X className="w-4 h-4" /></Button>
            </div>
            <iframe title="preview" srcDoc={previewHtml} className="w-full flex-1 bg-white" />
          </div>
        </div>
      )}

      {/* Run For User modal */}
      {runUserOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setRunUserOpen(false)} data-testid="run-user-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white text-base font-medium mb-3">Run digest for a single user</h3>
            <p className="text-xs text-white/50 mb-4">Bypasses opt-outs. Sends one real digest immediately.</p>
            <Input placeholder="user@email.com" value={runUserEmail} onChange={(e) => setRunUserEmail(e.target.value)} className="bg-white/5 border-white/10 text-white mb-4" data-testid="run-user-email-input" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setRunUserOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={runForUser} disabled={busySlug === runUserSlug} className={accentBtn} data-testid="run-user-confirm">Send</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div className="bg-white/[0.03] rounded px-2 py-1.5 text-center min-w-[70px]">
      <div className="text-white/40">{label}</div>
      <div className="text-white/90 font-mono text-sm mt-0.5">{value}</div>
    </div>
  );
}
